/**
 * Light / dark theme: one decision, applied one way, on every page.
 *
 * WHY THIS FILE EXISTS (2026-09-28). The same theme logic was copied into the
 * root layout's pre-paint script, LandingPageClient and MarketingShell, and the
 * site could end up showing both themes at once:
 *
 *   1. Switching theme on the homepage could leave the page background dark
 *      under light-mode text and cards. The shell transitioned its background
 *      colour over 200 ms and Chrome runs that transition on the compositor, on
 *      its own clock, while every other element flipped instantly. Measured in
 *      Chrome 153: the background held the old colour for 150 to 400 ms after
 *      the class changed, and on a real GPU it could stay there. applyTheme()
 *      now switches with every transition suppressed, so all of the page
 *      changes in the same frame.
 *   2. The signed-in app and the auth screens are designed light only (AppShell
 *      has no dark styles), but the pre-paint script added `dark` on every
 *      page, so on a Mac in dark mode the Settings cards and two Step 4 panels
 *      turned dark inside a light app. Those routes are now LIGHT-ONLY ZONES:
 *      `dark` is never applied while one is showing.
 *
 * The preference is the saved choice from the toggle, else the system setting.
 * Marketing pages follow it; light-only zones ignore it.
 */

export const THEME_STORAGE_KEY = "theme";

/** Set on <html> while a light-only zone is showing. */
export const THEME_ZONE_ATTR = "data-theme-zone";

/**
 * Route prefixes (after the locale prefix) that are always light: the app
 * behind sign-in, the auth screens, and internal tools. The pre-paint script
 * needs these because it runs before React knows which page it is on;
 * <LightOnlyTheme /> enforces the same thing when you arrive by client-side
 * navigation. `pnpm test:theme-zones` fails if an (app) route is missing here.
 */
export const LIGHT_ONLY_PATH_PREFIXES = [
  "/jobs",
  "/inventory",
  "/machines",
  "/knives",
  "/settings",
  "/seed",
  "/login",
  "/forgot-password",
  "/reset-password",
  "/auth",
  "/admin-tracking",
  "/envcheck",
  "/__envcheck",
] as const;

const LOCALE_PREFIX = /^\/(en|es|pt-BR|it|ru|de|tr)(?=\/|$)/;

export function isLightOnlyPath(pathname: string): boolean {
  const p = pathname.replace(LOCALE_PREFIX, "") || "/";
  return LIGHT_ONLY_PATH_PREFIXES.some((x) => p === x || p.startsWith(x + "/"));
}

/**
 * Runs in <head> before first paint (inlined by app/layout.tsx), so nothing is
 * ever painted in the wrong theme. Same rules as the functions below, written
 * out in ES5 because it runs before any bundle.
 */
export const THEME_INIT_SCRIPT = `(function(){try{var d=document.documentElement;var p=location.pathname.replace(${LOCALE_PREFIX.toString()},'')||'/';var L=${JSON.stringify(LIGHT_ONLY_PATH_PREFIXES)};var lo=false;for(var i=0;i<L.length;i++){if(p===L[i]||p.indexOf(L[i]+'/')===0){lo=true;break;}}if(lo){d.setAttribute('${THEME_ZONE_ATTR}','light');}var t=localStorage.getItem('${THEME_STORAGE_KEY}');var k=!lo&&(t==='dark'||(!t&&window.matchMedia('(prefers-color-scheme: dark)').matches));if(k){d.classList.add('dark');}else{d.classList.remove('dark');}}catch(e){}})();`;

function storedTheme(): "light" | "dark" | null {
  try {
    const t = localStorage.getItem(THEME_STORAGE_KEY);
    return t === "light" || t === "dark" ? t : null;
  } catch {
    return null;
  }
}

export function hasStoredTheme(): boolean {
  return storedTheme() !== null;
}

/** The visitor's preference: their saved choice, else the system setting. */
export function prefersDark(): boolean {
  const t = storedTheme();
  if (t) return t === "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function isLightOnlyZone(): boolean {
  return document.documentElement.getAttribute(THEME_ZONE_ATTR) === "light";
}

/**
 * Put `dark` on or off <html> with every CSS transition suppressed, so the
 * whole page changes in one frame instead of some elements animating on their
 * own clock (see note 1 above).
 */
export function applyTheme(dark: boolean): void {
  const root = document.documentElement;
  if (root.classList.contains("dark") === dark) return;
  const freeze = document.createElement("style");
  freeze.appendChild(
    document.createTextNode("*,*::before,*::after{transition:none!important}")
  );
  document.head.appendChild(freeze);
  root.classList.toggle("dark", dark);
  // Read a computed value so the new colours are resolved while transitions
  // are still off; only then let transitions back on.
  void window.getComputedStyle(root).color;
  window.setTimeout(() => freeze.remove(), 1);
}

/** Apply the preference, unless a light-only zone is showing. */
export function syncTheme(): void {
  applyTheme(!isLightOnlyZone() && prefersDark());
}

/** Save the toggle's choice and apply it. */
export function setThemePreference(dark: boolean): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, dark ? "dark" : "light");
  } catch {
    // Storage blocked (some private modes): still switch for this page view.
  }
  syncTheme();
}
